import { z } from "zod"

const linkSchema = z.string().max(2048).refine(value => {
	try { return ["https:", "http:", "mailto:"].includes(new URL(value).protocol) } catch { return false }
}, "Use an https, http or mailto link")
const markSchema = z.union([
	z.object({ type: z.enum(["bold", "italic"]) }).strict(),
	z.object({ type: z.literal("link"), attrs: z.object({
		href: linkSchema, target: z.literal("_blank").nullable().optional(),
		rel: z.string().max(100).nullable().optional(), class: z.null().optional(),
		title: z.string().max(2048).nullable().optional(),
	}).strict() }).strict(),
])
export type NoteMark = z.infer<typeof markSchema>
export type NoteNode = {
	type: "doc" | "paragraph" | "text" | "hardBreak" | "bulletList" | "orderedList" | "listItem"
	content?: NoteNode[]
	text?: string
	marks?: NoteMark[]
	attrs?: { start: number; type?: "1" | "a" | "A" | "i" | "I" | null | undefined }
}

// Bound recursion before parsing; notes support at most four nested list levels.
function nodeSchema(depth: number): z.ZodType<NoteNode> {
	const children = z.array(z.lazy(() => depth < 10 ? nodeSchema(depth + 1) : z.never())).max(500)
	return z.object({
		type: z.enum(["doc", "paragraph", "text", "hardBreak", "bulletList", "orderedList", "listItem"]),
		content: children.optional(), text: z.string().min(1).max(10000).optional(),
		marks: z.array(markSchema).max(3).optional(),
		attrs: z.object({ start: z.number().int().min(1).max(9999), type: z.enum(["1", "a", "A", "i", "I"]).nullable().optional() }).strict().optional(),
	}).strict().superRefine((node, ctx) => {
		const allowed: Record<NoteNode["type"], readonly string[]> = {
			doc: ["paragraph", "bulletList", "orderedList"], paragraph: ["text", "hardBreak"],
			bulletList: ["listItem"], orderedList: ["listItem"], listItem: ["paragraph", "bulletList", "orderedList"], text: [], hardBreak: [],
		}
		if (node.content?.some(child => !allowed[node.type].includes(child.type))
			|| (node.type === "text" ? node.text === undefined || node.content !== undefined : node.text !== undefined)
			|| (node.marks !== undefined && node.type !== "text")
			|| (node.attrs !== undefined && node.type !== "orderedList")
			|| (node.type === "hardBreak" && node.content !== undefined)
			|| (["doc", "bulletList", "orderedList", "listItem"].includes(node.type) && !node.content?.length)
			|| (node.type === "listItem" && node.content?.[0]?.type !== "paragraph")) {
			ctx.addIssue({ code: "custom", message: "Invalid note structure" })
		}
	}) as z.ZodType<NoteNode>
}
export function notePlainText(node: NoteNode): string {
	return node.text ?? (node.content?.map(notePlainText).join(" ") ?? "")
}
export const OrderNoteContentSchema = nodeSchema(0).refine(node => node.type === "doc", "Expected a note document")
	.refine(node => { const text = notePlainText(node).trim(); return text.length > 0 && text.length <= 10000 }, "Write a note of up to 10,000 characters")
	.refine(node => JSON.stringify(node).length <= 50000, "Note formatting is too large")
export const CreateOrderNoteSchema = z.object({ content: OrderNoteContentSchema }).strict()
export const UpdateOrderNoteSchema = CreateOrderNoteSchema.extend({ version: z.number().int().positive() })
export const DeleteOrderNoteQuerySchema = z.object({ version: z.coerce.number().int().positive() })
export const OrderNotesQuerySchema = z.object({ offset: z.coerce.number().int().min(0).max(100000).default(0) })
export const OrderNoteSchema = z.object({
	id: z.uuid(), orderId: z.uuid(), authorId: z.uuid(), authorName: z.string().nullable(),
	content: OrderNoteContentSchema, version: z.number().int().positive(),
	createdAt: z.iso.datetime(), updatedAt: z.iso.datetime(), canManage: z.boolean(),
})
export type OrderNote = z.infer<typeof OrderNoteSchema>
export const OrderNotesResponseSchema = z.object({ notes: z.array(OrderNoteSchema), hasMore: z.boolean() })
export const OrderNoteResponseSchema = z.object({ note: OrderNoteSchema })
